import { create } from 'zustand';
import type { UserTier, UserTierInfo } from '@machina/core';
import { getTierInfo } from '@machina/core';
import { supabase } from '../lib/supabase';

interface TierState {
  tier: UserTier;
  isLoaded: boolean;
  tierInfo: UserTierInfo;
  userEmail: string | null;
  loadTier: (userId: string, email?: string) => Promise<void>;
  setTier: (tier: UserTier) => void;
}

export const useTierStore = create<TierState>()((set, get) => ({
  tier: 'free',
  isLoaded: false,
  tierInfo: getTierInfo('free'),
  userEmail: null,

  loadTier: async (userId: string, email?: string) => {
    // Resolve email: param > cached > supabase auth session
    if (email) set({ userEmail: email });
    let resolvedEmail = email || get().userEmail || undefined;
    if (!resolvedEmail) {
      const { data: { user } } = await supabase.auth.getUser();
      resolvedEmail = user?.email || undefined;
      if (resolvedEmail) set({ userEmail: resolvedEmail });
    }

    try {
      const { data, error } = await supabase
        .from('machina_users')
        .select('tier')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        console.warn('[TierStore] Failed to load tier:', error.message);
        // Still apply alpha bypass even if DB lookup fails
        set({ isLoaded: true, tierInfo: getTierInfo('free', resolvedEmail) });
        return;
      }

      const tier = (data?.tier as UserTier) || 'free';
      set({ tier, isLoaded: true, tierInfo: getTierInfo(tier, resolvedEmail) });
      console.log('[TierStore] Loaded tier:', tier, resolvedEmail ? `(email: ${resolvedEmail})` : '');
    } catch (err) {
      console.warn('[TierStore] Unexpected error:', err);
      set({ isLoaded: true, tierInfo: getTierInfo('free', resolvedEmail) });
    }
  },

  setTier: (tier: UserTier) => {
    set({ tier, tierInfo: getTierInfo(tier, get().userEmail || undefined) });
  },
}));
